import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Header } from './Header';

interface LayoutProps {
  /**
   * Page content to render
   */
  children: ReactNode;

  /**
   * Current active page for header navigation
   */
  activePage?: 'dashboard' | 'courses' | 'feedback' | 'preferences' | 'progress' | null;

  /**
   * Show header navigation links
   * Default: true
   */
  showNav?: boolean;

  /**
   * Show user actions in header
   * Default: true
   */
  showUserActions?: boolean;

  /**
   * Custom content for header right side
   */
  headerRightContent?: ReactNode;

  /**
   * Background color for the page
   * Default: var(--cld-color-off-white)
   */
  backgroundColor?: string;

  /**
   * Maximum width for content container
   * Default: '7xl' (1280px)
   */
  maxWidth?: 'full' | '7xl' | '6xl' | '5xl' | '4xl';

  /**
   * Apply padding to content area
   * Default: true
   */
  padded?: boolean;

  /**
   * Additional CSS classes for content container
   */
  contentClassName?: string;

  /**
   * Optional sub-header content (rendered between header and main content)
   * Useful for sticky KPI bars, breadcrumbs, etc.
   */
  subHeader?: ReactNode;

  /**
   * Show footer with legal links
   * Default: true
   */
  showFooter?: boolean;
}

/**
 * Layout Component
 *
 * Standard page layout wrapper for ClosedLoopDiving application.
 * Features:
 * - Branded header with navigation
 * - Consistent content container with max-width
 * - Responsive padding using design tokens
 * - Customizable background and layout options
 */
export function Layout({
  children,
  activePage = null,
  showNav = true,
  showUserActions = true,
  headerRightContent,
  backgroundColor = 'var(--cld-color-off-white)',
  maxWidth = '7xl',
  padded = true,
  contentClassName = '',
  subHeader,
  showFooter = true,
}: LayoutProps) {
  const maxWidthClasses = {
    full: 'max-w-full',
    '7xl': 'max-w-7xl',
    '6xl': 'max-w-6xl',
    '5xl': 'max-w-5xl',
    '4xl': 'max-w-4xl',
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor,
        fontFamily: 'var(--cld-font-body)',
      }}
    >
      <Header
        activePage={activePage}
        showNav={showNav}
        showUserActions={showUserActions}
        rightContent={headerRightContent}
      />

      {subHeader}

      <main
        className={`${maxWidthClasses[maxWidth]} mx-auto ${contentClassName}`}
        style={{
          padding: padded
            ? `var(--cld-spacing-xl) var(--cld-spacing-lg)`
            : undefined,
          minHeight: showFooter ? 'calc(100vh - 200px)' : undefined,
        }}
      >
        {children}
      </main>

      {showFooter && (
        <footer
          style={{
            backgroundColor: 'var(--cld-color-primary)',
            padding: 'var(--cld-spacing-lg) var(--cld-spacing-xl)',
            marginTop: 'auto',
          }}
        >
          <div
            className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4"
          >
            <div
              className="text-sm"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {new Date().getFullYear()} ClosedLoopDiving. All rights reserved.
            </div>
            <nav className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-sm hover:underline"
                style={{ color: 'rgba(255, 255, 255, 0.7)', transition: 'var(--cld-transition-fast)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cld-color-white)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-sm hover:underline"
                style={{ color: 'rgba(255, 255, 255, 0.7)', transition: 'var(--cld-transition-fast)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cld-color-white)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
              >
                Privacy
              </Link>
              <Link
                to="/about"
                className="text-sm hover:underline"
                style={{ color: 'rgba(255, 255, 255, 0.7)', transition: 'var(--cld-transition-fast)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cld-color-white)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
              >
                About
              </Link>
              <a
                href="mailto:support@closedloopdiving.com"
                className="text-sm hover:underline"
                style={{ color: 'rgba(255, 255, 255, 0.7)', transition: 'var(--cld-transition-fast)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cld-color-white)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
              >
                Contact
              </a>
            </nav>
          </div>
        </footer>
      )}
    </div>
  );
}
